import React, { useState, useEffect } from 'react';
import { Volume2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_67da558a-9e84-415d-a51c-097c2aae8691/artifacts/tg6gwsri_ChatGPT%20Image%20Feb%2027%2C%202026%2C%2002_12_10%20PM.png";

// Fixed bar heights for consistent animation
const BAR_HEIGHTS = [24, 32, 40, 28, 36, 36, 28, 40, 32, 24];

export function PreRollSplash({ onComplete, adFree }) {
  const [countdown, setCountdown] = useState(5);
  const [canSkip, setCanSkip] = useState(false);

  useEffect(() => {
    // Check if already shown this session
    const hasShown = sessionStorage.getItem('preroll_shown');
    if (hasShown || adFree) {
      onComplete();
      return;
    }

    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanSkip(true);
          // Auto-continue after 2 more seconds
          setTimeout(() => {
            sessionStorage.setItem('preroll_shown', 'true');
            onComplete();
          }, 2000);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Allow skip after 3 seconds
    setTimeout(() => setCanSkip(true), 3000);

    return () => clearInterval(timer);
  }, [onComplete, adFree]);

  const handleSkip = () => {
    sessionStorage.setItem('preroll_shown', 'true');
    onComplete();
  };

  // Don't render if ad-free
  if (adFree) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'var(--background)' }}
      data-testid="preroll-splash"
    >
      {/* Background gradient */}
      <div 
        className="absolute inset-0"
        style={{
          background: `radial-gradient(ellipse at 30% 30%, var(--primary) 0%, transparent 50%),
                       radial-gradient(ellipse at 70% 70%, var(--secondary) 0%, transparent 50%)`
        }}
      />

      <div className="relative z-10 text-center max-w-md px-6">
        {/* Logo */}
        <img 
          src={LOGO_URL}
          alt="DJ's & MC Live"
          className="w-40 h-40 mx-auto mb-6 object-contain"
          style={{ filter: 'drop-shadow(0 0 30px var(--primary))' }}
        />

        {/* Station Name - removed since logo has text */}
        <p className="text-lg mb-6" style={{ color: 'var(--text-muted)' }}>
          Nashville Hip-Hop Radio
        </p>

        {/* Animated sound waves */}
        <div className="flex items-center justify-center gap-1 mb-8">
          {BAR_HEIGHTS.slice(0, 5).map((height, i) => (
            <div
              key={i}
              className="w-1 rounded-full sound-bar"
              style={{
                height: `${height}px`,
                background: `linear-gradient(to top, var(--primary), var(--secondary))`,
                animationDelay: `${i * 0.15}s`
              }}
            />
          ))}
          <Volume2 className="w-5 h-5 mx-2" style={{ color: 'var(--primary)' }} />
          {BAR_HEIGHTS.slice(5, 10).map((height, i) => (
            <div
              key={i + 5}
              className="w-1 rounded-full sound-bar"
              style={{
                height: `${height}px`,
                background: `linear-gradient(to top, var(--primary), var(--secondary))`,
                animationDelay: `${(i + 5) * 0.15}s`
              }}
            />
          ))}
        </div>

        {/* Sponsor mention */}
        <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
          Powered by our amazing sponsors
        </p>

        {/* Skip/Enter button */}
        <button
          onClick={handleSkip}
          disabled={!canSkip}
          className="px-8 py-3 rounded-xl font-bold transition-all hover:scale-105 disabled:opacity-50"
          style={{ 
            background: canSkip 
              ? 'linear-gradient(135deg, var(--primary), var(--secondary))' 
              : 'var(--surface-highlight)',
            color: 'white'
          }}
          data-testid="preroll-skip"
        >
          {canSkip ? 'Enter Station' : `Starting in ${countdown}...`}
        </button>

        {/* Donate to skip ads */}
        <p className="text-xs mt-4" style={{ color: 'var(--text-muted)' }}>
          Donate to enjoy ad-free listening
        </p>
      </div>

      <style>{`
        @keyframes soundBar {
          0%, 100% {
            transform: scaleY(0.4);
            opacity: 0.6;
          }
          50% {
            transform: scaleY(1);
            opacity: 1;
          }
        }
        .sound-bar {
          animation: soundBar 0.8s ease-in-out infinite;
          transform-origin: bottom;
        }
      `}</style>
    </div>
  );
}
